import { FC, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, IconButton, Tooltip, Typography, Drawer } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import CreatedByCell from "./CreatedByCell";
import LocationSelect from "./pickers/LocationSelect";
import { FilterBar, FilterSearch, FilterSelect } from "./InvFilters";
import AuditTimeline from "../AuditTimeline/AuditTimeline";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchRequisitions } from "../../store/requisitionStore/requisitionThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";

const STATUS_COLOR: Record<string, string> = {
  draft: "#b08900",
  submitted: "#488dff",
  approved: "#49a563",
  partially_fulfilled: "#0d9488",
  fulfilled: "#2e7d32",
  rejected: "#c33b3b",
};
const REQ_STATUSES = ["draft", "submitted", "approved", "partially_fulfilled", "fulfilled", "rejected"];
const statusLabel = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const RequisitionList: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { requisitions, status, rowCount } = useAppSelector((state) => state.requisitions);

  // Server-side filters (resolved on the backend via query params).
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Server-side pagination (0-based page); default 25 rows/page.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditRequisition, setAuditRequisition] = useState<any>(null);

  const handleOpenAudit = (row: any) => {
    setAuditRequisition(row);
    setAuditDialogOpen(true);
  };

  const handleCloseAudit = () => {
    setAuditDialogOpen(false);
    setAuditRequisition(null);
  };

  const buildParams = () => {
    const p: string[] = [];
    if (debouncedSearch) p.push(`search=${encodeURIComponent(debouncedSearch)}`);
    if (fromLocationId) p.push(`fromLocationId=${fromLocationId}`);
    if (toLocationId) p.push(`toLocationId=${toLocationId}`);
    if (statusFilter) p.push(`status=${statusFilter}`);
    p.push(`page=${page}`);
    p.push(`limit=${pageSize}`);
    return p.join("&");
  };

  const hasFilters = !!(debouncedSearch || fromLocationId || toLocationId || statusFilter);
  const resetFilters = () => {
    setSearch("");
    setFromLocationId("");
    setToLocationId("");
    setStatusFilter("");
    setPage(0);
  };
  // Filter changes reset to the first page.
  const onFrom = (v: string) => { setFromLocationId(v); setPage(0); };
  const onTo = (v: string) => { setToLocationId(v); setPage(0); };
  const onStatus = (v: string) => { setStatusFilter(v); setPage(0); };
  const onPageSizeChange = (n: number) => { setPageSize(n); setPage(0); };

  // Debounce only the free-text search (resets to page 0 once it settles).
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load when filters or pagination change.
  useEffect(() => {
    dispatch(fetchRequisitions(buildParams()));
  }, [dispatch, debouncedSearch, fromLocationId, toLocationId, statusFilter, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "reqNumber",
        headerName: "Req #",
        flex: 1.2,
        minWidth: 130,
        renderCell: ({ row }: GridValueGetterParams) => (
          <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/requisitions/${row._id}`)}>
            {row.reqNumber}
          </span>
        ),
      },
      { field: "from", headerName: "From (requester)", flex: 1, minWidth: 130, valueGetter: (p: GridValueGetterParams) => p.row.fromLocationId?.name || "" },
      { field: "to", headerName: "To (fulfiller)", flex: 1, minWidth: 130, valueGetter: (p: GridValueGetterParams) => p.row.toLocationId?.name || "" },
      { field: "items", headerName: "Items", flex: 0.6, minWidth: 80, valueGetter: (p: GridValueGetterParams) => (p.row.items || []).length },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 130,
        renderCell: ({ row }: GridValueGetterParams) => <Chip size="small" label={(row.status || "").replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())} sx={{ color: STATUS_COLOR[row.status] || "#555" }} />,
      },
      // { field: "createdBy", headerName: "Created By", flex: 1.2, minWidth: 170, sortable: false, renderCell: ({ row }: GridValueGetterParams) => <CreatedByCell createdBy={row.createdBy} createdAt={row.createdAt} /> },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.6,
        minWidth: 85,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) => {
          const st = (row.status || "").toLowerCase();
          let actionTitle = "View Requisition";
          if (st === "draft") actionTitle = "Submit Requisition";
          else if (st === "submitted") actionTitle = "Approve Requisition";
          else if (st === "approved" || st === "partially_fulfilled") actionTitle = "Fulfill Requisition";

          return (
            <>
              <Tooltip title={actionTitle}>
                <IconButton
                  style={{ color: "#488dff", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", height: 35, width: 35, marginRight: 8 }}
                  size="small"
                  onClick={() => navigate(`/inventory/requisitions/${row._id}`)}
                >
                  <ArrowForwardIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Audit Log">
                <IconButton
                  style={{ color: "#6b7280", backgroundColor: "#f5f5f5", border: "1px solid #e0e0e0", height: 35, width: 35 }}
                  size="small"
                  onClick={() => handleOpenAudit(row)}
                >
                  <AssignmentIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <FilterBar
        onReset={resetFilters}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button startIcon={<AssessmentOutlinedIcon />} size="small" variant="outlined" onClick={() => navigate("/inventory/requisitions/report")}>
              View Report
            </Button>
            <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => navigate("/inventory/requisitions/new")}>
              New Requisition
            </Button>
          </Box>
        }
      >
        <FilterSearch value={search} onChange={setSearch} placeholder="Search Req #" width={200} />
        <LocationSelect borderless width={180} value={fromLocationId} onChange={onFrom} label="From (requester)" activeOnly={false} />
        <LocationSelect borderless width={180} value={toLocationId} onChange={onTo} label="To (fulfiller)" activeOnly={false} />
        <FilterSelect
          value={statusFilter}
          onChange={onStatus}
          placeholder="Status"
          width={170}
          options={REQ_STATUSES.map((s) => ({ label: statusLabel(s), value: s }))}
        />
      </FilterBar>
      {status !== ApiStatus.LOADING && (requisitions?.length || 0) === 0 ? (
        hasFilters ? (
          <InventoryEmptyState
            icon={<AssignmentOutlinedIcon />}
            title="No Requisitions Found"
            description="No requisitions match the current filters. Try adjusting your search, locations, or status."
            caption="Need help? Check the Guide tab on inventory management"
          />
        ) : (
          <InventoryEmptyState
            icon={<AssignmentOutlinedIcon />}
            title="No Requisitions Yet"
            description="Raise your first requisition to request stock from another location and keep your outlets supplied."
            actionLabel="Create Your First Requisition"
            onAction={() => navigate("/inventory/requisitions/new")}
            caption="Need help? Check the Guide tab on inventory management"
          />
        )
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={requisitions}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(p: number) => setPage(p)}
            onPageSizeChange={onPageSizeChange}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}
      <Drawer
        anchor="right"
        open={auditDialogOpen}
        onClose={handleCloseAudit}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, maxWidth: "100%" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2, px: 3, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e5e7eb" }}>
            <AssignmentIcon sx={{ color: "#6b7280", fontSize: 26 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", fontSize: "1.1rem" }}>Audit Logs</Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseAudit}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", py: 2, px: 2 }}>
            {auditRequisition && (
              <AuditTimeline entity="Requisition" entityId={auditRequisition._id} />
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default RequisitionList;
